import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const [filterDate, setFilterDate] = useState('all'); // all, upcoming, past
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = pagination.current_page;
        const last = pagination.last_page;
        const maxPages = 3;

        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, end - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button className="page-link text-gray-900" onClick={() => fetchEvents(current - 1)}>Previous</button>
                    </li>

                    {start > 1 && (
                        <>
                            <li className="page-item"><button className="page-link  text-gray-900" onClick={() => fetchEvents(1)}>1</button></li>
                            {start > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                        </>
                    )}

                    {pageNumbers.map(num => (
                        <li
                            key={num}
                            className={`page-item ${num === current ? 'active' : ''}`}
                        >
                            <button
                                className={`page-link text-gray-900 ${
                                    num === current ? '!bg-yellow-400 !text-white !border-yellow-400 border' : ''
                                }`}
                                onClick={() => fetchEvents(num)}
                            >
                                {num}
                            </button>
                        </li>
                    ))}

                    {end < last && (
                        <>
                            {end < last - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                            <li className="page-item"><button className="page-link  text-gray-900" onClick={() => fetchEvents(last)}>{last}</button></li>
                        </>
                    )}

                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button className="page-link  text-gray-900" onClick={() => fetchEvents(current + 1)}>Next</button>
                    </li>
                </ul>
            </nav>
        );
    };

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, []);

    const fetchEvents = async (pageNumber = 1) => {
        try {
            setLoading(true);
            const params = {
                page: pageNumber,
                searchTerm: debouncedSearch || undefined,
                status: filterStatus !== 'all' ? filterStatus : undefined,
                date: filterDate !== 'all' ? filterDate : undefined,
                category: filterCategory !== 'all' ? filterCategory : undefined,
            };

            const response = await api.get('/organizer/events', { params });
            const paginator = response.data.data; // make sure API returns pagination info
            const statsFromApi = response.data.stats;
            setEvents(paginator.data || []);
            setPagination(paginator);
            setPage(pageNumber);
            setStats(statsFromApi);
        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('Failed to load events');
            setEvents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents(1); // reset to first page when filters/search change
    }, [debouncedSearch, filterStatus, filterDate, filterCategory]);

    const fetchCategories = async () => {
    try {
      const response = await api.get('/events/categories');
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

    const handleToggleStatus = async (eventId, currentStatus) => {
        const action = currentStatus ? 'Disable' : 'Enable';
        const result = await Swal.fire({
            title: `${action} Event`,
            text: `Are you sure you want to ${action.toLowerCase()} this event?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.put(`/organizer/events/${eventId}`, { status: !currentStatus });
            toast.success(`Event ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
            fetchEvents();
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update event status');
        }
    };

    const handleDeleteEvent = async (eventId, eventTitle) => {
        const result = await Swal.fire({
            title: 'Delete Event',
            text: `Are you sure you want to delete "${eventTitle}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(`/organizer/events/${eventId}`);
            toast.success('Event deleted successfully');
            fetchEvents();
        } catch (error) {
            console.error('Error deleting event:', error);
            toast.error('Failed to delete event');
        }
    };

  const handleDuplicateEvent = async (eventId) => {
    if (window.confirm('Do you want to create a copy of this event?')) {
      try {
        // Fetch the event details
        const response = await api.get(`/organizer/events/${eventId}`);
        const event = response.data.data;
        
        // Create a new event with copied data
        const newEventData = {
          ...event,
          title: `${event.title} (Copy)`,
          status: false, // Set as inactive by default
        };
        
        delete newEventData.id;
        delete newEventData.created_at;
        delete newEventData.updated_at;
        delete newEventData.images;
        delete newEventData.ticket_categories;
        
        const createResponse = await api.post('/organizer/events', newEventData);
        toast.success('Event duplicated successfully');
        fetchEvents();
      } catch (error) {
        console.error('Error duplicating event:', error);
        toast.error('Failed to duplicate event');
      }
    }
  };

  // Statistics
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        upcoming: 0,
    });


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h5 className="h5 font-bold font-weight-bold text-gray-900">My Events</h5>
          <p className="text-gray-600 mt-1">Manage your events and track performance</p>
        </div>
        <Link
          to="/organizer/events/create"
          className="btn !text-white !font-weight-bold !bg-yellow-400 shadow-sm hover:!bg-yellow-500 flex items-center gap-2"
        >
          <span className="text-xl mr-2">+</span>
          Create Event
        </Link>
      </div>

      {/* Statistics Cards */}
        <div className="row mb-4">
            {/* Total Events Card */}
            <div className="col-xl-3 col-md-6 mb-4">
                <div className="card border-left-yellow shadow h-100 py-2">
                    <div className="card-body">
                        <div className="row no-gutters align-items-center">
                            <div className="col mr-2">
                                <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                                    Total Events
                                </div>
                                <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.total}</div>
                            </div>
                            <div className="col-auto">
                                <i className="fas fa-calendar fa-2x text-gray-300"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Active Events Card */}
            <div className="col-xl-3 col-md-6 mb-4">
                <div className="card border-left-yellow shadow h-100 py-2">
                    <div className="card-body">
                        <div className="row no-gutters align-items-center">
                            <div className="col mr-2">
                                <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                                    Active Events
                                </div>
                                <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.active}</div>
                            </div>
                            <div className="col-auto">
                                <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tickets Sold Card */}
            <div className="col-xl-3 col-md-6 mb-4">
                <div className="card border-left-yellow shadow h-100 py-2">
                    <div className="card-body">
                        <div className="row no-gutters align-items-center">
                            <div className="col mr-2">
                                <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                                    Inactive Events
                                </div>
                                <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.inactive}</div>
                            </div>
                            <div className="col-auto">
                                <i className="fas fa-ticket-alt fa-2x text-gray-300"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Total Revenue Card */}
            <div className="col-xl-3 col-md-6 mb-4">
                <div className="card border-left-yellow shadow h-100 py-2">
                    <div className="card-body">
                        <div className="row no-gutters align-items-center">
                            <div className="col mr-2">
                                <div className="text-xs font-weight-bold text-gray-800 text-uppercase mb-1">
                                    Upcoming Events
                                </div>
                                <div className="h5 mb-0 font-weight-bold text-gray-800">
                                    {stats.upcoming}
                                </div>
                            </div>
                            <div className="col-auto">
                                <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Search
                    </label>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search events..."
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-yellow-400"
                    />
                </div>

                {/* Status Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                    </label>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-yellow-400"
                    >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>

                {/* Date Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date
                    </label>
                    <select
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-yellow-400"
                    >
                        <option value="all">All Dates</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="past">Past</option>
                    </select>
                </div>

                {/* Category Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                    </label>
                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-yellow-400"
                    >
                        <option value="all">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Clear Filters */}
            {(searchTerm || filterStatus !== 'all' || filterDate !== 'all' || filterCategory !== 'all') && (
                <div className="mt-4">
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setFilterStatus('all');
                            setFilterDate('all');
                            setFilterCategory('all');
                        }}
                        className="text-sm text-red-600 hover:text-red-700"
                    >
                        × Clear all filters
                    </button>
                </div>
            )}
        </div>


        {/* Events List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="card-body">
                {loading ? (
                    <div className="text-center py-12">
                        <svg
                            className="animate-spin h-12 w-12 mx-auto text-yellow-400"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <p className="mt-4 text-gray-600">Loading events...</p>
                    </div>
                ) : events.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">📅</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {searchTerm || filterStatus !== 'all' || filterDate !== 'all' || filterCategory !== 'all'
                                ? 'No events found'
                                : 'No events yet'}
                        </h3>
                        <p className="text-gray-600 mb-6">
                            {searchTerm || filterStatus !== 'all' || filterDate !== 'all' || filterCategory !== 'all'
                                ? 'Try adjusting your filters'
                                : 'Create your first event to get started'}
                        </p>
                        {!searchTerm && filterStatus === 'all' && filterDate === 'all' && filterCategory === 'all' && (
                            <Link to="/organizer/events/create" className="btn btn-primary">
                                Create Your First Event
                            </Link>
                        )}
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="thead-light">
                            <tr>
                                <th>Image</th>
                                <th>Event</th>
                                <th>Date</th>
                                <th>Location</th>
                                <th>Status</th>
                                <th className="text-center">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {events.map((event) => (
                                <tr key={event.id}>
                                    <td>
                                        {event.images && event.images.length > 0 ? (
                                            <img
                                                src={event.images[0].image_url}
                                                alt={event.title}
                                                style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 5 }}
                                            />
                                        ) : (
                                            <div
                                                className="bg-gray-200 flex items-center justify-center"
                                                style={{ width: 50, height: 50, borderRadius: 5 }}
                                            >
                                                <span>🎫</span>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <div className="font-weight-bold text-gray-800">{event.title}</div>
                                        {event.category?.category_name && (
                                            <span className="badge badge-primary">{event.category.category_name}</span>
                                        )}
                                        {event.top_event && (
                                            <span className="badge badge-primary mr-2">
                      <i className="fas fa-fire mr-1"></i>Top
                    </span>
                                        )}
                                        {event.featured_event && (
                                            <span className="badge badge-warning mr-2">
                      <i className="fas fa-star mr-1"></i>Featured
                    </span>
                                        )}
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            <i className="far fa-calendar mr-1"></i>
                                            {new Date(event.event_start).toLocaleDateString()}{' '}
                                            {new Date(event.event_start).toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </small>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            <i className="fas fa-map-marker-alt mr-1"></i>
                                            {event.location || 'No location'}
                                        </small>
                                    </td>
                                    <td>
                  <span className={`badge ${event.status ? 'badge-success' : 'badge-warning'}`}>
                    {event.status ? 'Active' : 'Inactive'}
                  </span>
                                    </td>
                                    <td className="text-center">
                                        <div className="btn-group" role="group">
                                            <Link
                                                to={`/organizer/events/edit/${event.id}`}
                                                className="btn btn-sm btn-outline-primary"
                                                title="Edit Event"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </Link>
                                            <Link
                                                to={`/events/${event.id}`}
                                                className="btn btn-sm btn-outline-info"
                                                title="View Event"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </Link>
                                            <Link
                                                to={`/organizer/events/${event.id}/seat-map`}
                                                className="btn btn-sm"
                                                title="Seat Map"
                                                style={{ border: '1px solid #6c757d', color: '#6c757d' }}
                                                onMouseEnter={e => { e.currentTarget.style.color = '#0d6efd'; e.currentTarget.style.borderColor = '#0d6efd'; }}
                                                onMouseLeave={e => { e.currentTarget.style.color = '#6c757d'; e.currentTarget.style.borderColor = '#6c757d'; }}
                                            >
                                                <i className="fas fa-map-marked-alt"></i>
                                            </Link>
                                            <button
                                                onClick={() => handleToggleStatus(event.id, event.status)}
                                                className={`btn btn-sm ${event.status ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                title={event.status ? 'Disable Event' : 'Enable Event'}
                                            >
                                                <i className={`fas ${event.status ? 'fa-ban' : 'fa-check-circle'}`}></i>
                                            </button>
                                            <button
                                                onClick={() => handleDeleteEvent(event.id)}
                                                className="btn btn-sm btn-outline-danger"
                                                title="Delete Event"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        {renderPagination()}
                    </div>
                )}
            </div>
        </div>


        {/* Results Count */}
      {events.length > 0 && (
        <div className="text-sm text-gray-600">
          Showing {events.length} of {events.length} events
        </div>
      )}
    </div>
  );
};

export default Events;

